import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { ColumnDef } from "@tanstack/react-table";
import { MoreHorizontal, Pencil, Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

interface Company {
  id: string;
  name: string;
  description: string;
  industry: string;
  website: string;
  ownership_type: string;
  ownership_type_other?: string;
  created_at: string;
}

export default function CompanyManagement() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [companyToDelete, setCompanyToDelete] = useState<Company | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    industry: "",
    website: "",
    ownership_type: "",
    ownership_type_other: ""
  });
  const { toast } = useToast();

  const fetchCompanies = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("companies")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setCompanies(data || []);
    } catch (error) {
      console.error("Error fetching companies:", error);
      toast({
        title: "Fehler",
        description: "Unternehmen konnten nicht geladen werden.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const openCreateDialog = () => {
    setEditingCompany(null);
    setFormData({
      name: "",
      description: "",
      industry: "",
      website: "",
      ownership_type: "",
      ownership_type_other: ""
    });
    setDialogOpen(true);
  };

  const openEditDialog = (company: Company) => {
    setEditingCompany(company);
    setFormData({
      name: company.name,
      description: company.description || "",
      industry: company.industry || "",
      website: company.website || "",
      ownership_type: company.ownership_type || "",
      ownership_type_other: company.ownership_type_other || ""
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (company: Company) => {
    setCompanyToDelete(company);
    setDeleteDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editingCompany) {
        // Update existing company
        const { error } = await supabase
          .from("companies")
          .update(formData)
          .eq("id", editingCompany.id);

        if (error) throw error;

        toast({
          title: "Erfolg",
          description: "Unternehmen wurde aktualisiert."
        });
      } else {
        // Create new company
        const { error } = await supabase.from("companies").insert([formData]);

        if (error) throw error;

        toast({
          title: "Erfolg",
          description: "Unternehmen wurde erstellt."
        });
      }

      setDialogOpen(false);
      fetchCompanies();
    } catch (error) {
      console.error("Error saving company:", error);
      toast({
        title: "Fehler",
        description: "Unternehmen konnte nicht gespeichert werden.",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async () => {
    if (!companyToDelete) return;

    try {
      const { error } = await supabase
        .from("companies")
        .delete()
        .eq("id", companyToDelete.id);

      if (error) throw error;

      toast({
        title: "Erfolg",
        description: "Unternehmen wurde gelöscht."
      });

      setDeleteDialogOpen(false);
      setCompanyToDelete(null);
      fetchCompanies();
    } catch (error) {
      console.error("Error deleting company:", error);
      toast({
        title: "Fehler",
        description: "Unternehmen konnte nicht gelöscht werden.",
        variant: "destructive"
      });
    }
  };

  const columns: ColumnDef<Company>[] = [
    {
      accessorKey: "name",
      header: "Name"
    },
    {
      accessorKey: "industry",
      header: "Branche",
      cell: ({ row }) => {
        const industry = row.getValue("industry") as string;
        switch (industry) {
          case "technology":
            return "Technologie";
          case "healthcare":
            return "Gesundheitswesen";
          case "finance":
            return "Finanzen";
          case "education":
            return "Bildung";
          case "other":
            return "Sonstiges";
          default:
            return industry || "-";
        }
      }
    },
    {
      accessorKey: "ownership_type",
      header: "Trägerschaft",
      cell: ({ row }) => {
        const ownershipType = row.getValue("ownership_type") as string;
        const company = row.original;
        switch (ownershipType) {
          case "freigemeinnuetzig":
            return "Freigemeinnützig";
          case "oeffentlich":
            return "Öffentlich";
          case "privat":
            return "Privat";
          case "other":
            return company.ownership_type_other || "Sonstige";
          default:
            return ownershipType || "-";
        }
      }
    },
    {
      accessorKey: "website",
      header: "Website",
      cell: ({ row }) => {
        const website = row.getValue("website") as string;
        return website ? (
          <a
            href={website}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            {website}
          </a>
        ) : (
          "-"
        );
      }
    },
    {
      accessorKey: "created_at",
      header: "Erstellt",
      cell: ({ row }) => {
        const date = new Date(row.getValue("created_at") as string);
        return date.toLocaleDateString("de-DE");
      }
    },
    {
      id: "actions",
      enableHiding: false,
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreHorizontal size={16} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => openEditDialog(row.original)}>
              <Pencil className="mr-2 h-4 w-4" />
              Bearbeiten
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-red-600"
              onClick={() => openDeleteDialog(row.original)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Löschen
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )
    }
  ];

  if (loading) {
    return <div>Lade Unternehmen...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold">Unternehmen verwalten</h2>
          <p className="text-gray-500">Verwalte alle Unternehmen im System.</p>
        </div>
        <Button onClick={openCreateDialog}>
          <Plus className="mr-2 h-4 w-4" />
          Neues Unternehmen
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={companies}
        searchKey="name"
        filterPlaceholder="Unternehmen suchen..."
      />

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingCompany ? "Unternehmen bearbeiten" : "Neues Unternehmen"}
            </DialogTitle>
            <DialogDescription>
              {editingCompany
                ? "Bearbeite die Unternehmensdaten."
                : "Erstelle ein neues Unternehmen."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-6 py-4">
            <div className="space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="name">Firmenname *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="Firmennamen eingeben"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ownership_type">Trägerschaft</Label>
                <Select
                  value={formData.ownership_type}
                  onValueChange={(value: string) =>
                    setFormData({ ...formData, ownership_type: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Trägerschaft auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="freigemeinnuetzig">
                      Freigemeinnützig
                    </SelectItem>
                    <SelectItem value="oeffentlich">Öffentlich</SelectItem>
                    <SelectItem value="privat">Privat</SelectItem>
                    <SelectItem value="other">Sonstige</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.ownership_type === "other" && (
                <div className="grid gap-2">
                  <Label htmlFor="ownership_type_other">
                    Sonstige Trägerschaft
                  </Label>
                  <Input
                    id="ownership_type_other"
                    value={formData.ownership_type_other}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        ownership_type_other: e.target.value
                      })
                    }
                    placeholder="Bitte spezifizieren"
                  />
                </div>
              )}
              <div className="grid gap-2">
                <Label htmlFor="industry">Branche</Label>
                <Select
                  value={formData.industry}
                  onValueChange={(value: string) =>
                    setFormData({ ...formData, industry: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Branche auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="technology">Technologie</SelectItem>
                    <SelectItem value="healthcare">Gesundheitswesen</SelectItem>
                    <SelectItem value="finance">Finanzen</SelectItem>
                    <SelectItem value="education">Bildung</SelectItem>
                    <SelectItem value="other">Sonstiges</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  type="url"
                  value={formData.website}
                  onChange={(e) =>
                    setFormData({ ...formData, website: e.target.value })
                  }
                  placeholder="https://beispiel.de"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Beschreibung</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Unternehmensbeschreibung eingeben"
                className="min-h-[200px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleSave} disabled={!formData.name}>
              {editingCompany ? "Aktualisieren" : "Erstellen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unternehmen löschen</DialogTitle>
            <DialogDescription>
              Sind Sie sicher, dass Sie "{companyToDelete?.name}" löschen
              möchten? Diese Aktion kann nicht rückgängig gemacht werden.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Abbrechen
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
